import { X } from '@phosphor-icons/react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useUser } from '../../../../../utils/UserContext';
import useApi from '../../../../../utils/AxiosWrapper';
import { useQueryClient } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';

type BrokerForm = {
  broker_name: string;
  broker_email: string;
  broker_phone: string;
  broker_website: string;
};

export const AddBrokerModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<BrokerForm>();

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const onSubmit = async (data: BrokerForm) => {
    setLoading(true);
    try {
      await callProtectedApi(
        `/broker-contacts/${userId}`,
        'post',
        {
          broker_name: data.broker_name,
          broker_email: data.broker_email || undefined,
          broker_phone: data.broker_phone || undefined,
          broker_website: data.broker_website || undefined,
        },
        'Error submitting broker contact.'
      );
      toast.success('Broker submitted successfully!');
      queryClient.invalidateQueries({ queryKey: ['brokerContacts', userId] });
      reset();
      onClose();
    } catch (error) {
      console.error('Error submitting broker:', error);
      toast.error('Failed to submit broker. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-gray-900 bg-opacity-50 sm:items-center">
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-lg bg-white shadow-lg sm:mx-4 sm:max-w-2xl sm:rounded-md">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Add Broker Contact
          </h2>
          <X
            size={24}
            weight="regular"
            className="cursor-pointer text-gray-500 hover:opacity-80"
            onClick={onClose}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-4 sm:px-6">
          <div className="space-y-4">
            {/* Broker Name */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Broker Name <span className="text-red-500">*</span>
              </label>
              <input
                {...register('broker_name', {
                  required: 'Broker name is required',
                })}
                type="text"
                placeholder="Enter broker name"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
              {errors.broker_name && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.broker_name.message}
                </p>
              )}
            </div>

            {/* Broker Email */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Primary Contact Email
              </label>
              <input
                {...register('broker_email', {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                  },
                })}
                type="email"
                placeholder="real-person@broker.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
              {errors.broker_email && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.broker_email.message}
                </p>
              )}
            </div>

            {/* Broker Phone */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Primary Contact Phone
              </label>
              <input
                {...register('broker_phone', {
                  onChange: (e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    setValue('broker_phone', formatted);
                  },
                })}
                type="tel"
                placeholder="(555) 123-4567"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>

            {/* Broker Website */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Broker Website
              </label>
              <input
                {...register('broker_website')}
                type="text"
                placeholder="www.broker.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
              }}
              className="rounded-full border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center rounded-full bg-accent px-6 py-2.5 text-sm font-medium text-black hover:opacity-90 disabled:opacity-70"
            >
              {loading ? (
                <ClipLoader size={14} color="#ffffff" />
              ) : (
                'Submit Broker'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
